import { Attribute, useConfigurator } from 'views/ProductPage/configurators/useConfigurator';
import {
  Validate,
  isValueConstraint,
  ValueConstraint
} from 'views/ProductPage/configurators/rules';
import { getConfiguratorVariables } from 'views/ProductPage/selector';
import { NumericInput } from 'views/ProductPage/configurators/numericInput';
import connect from 'components/Shared/connect';
import { onBlurField, onSelectValue } from 'views/ProductPage/configurators/actions';
import { IConstraint } from 'models/IConstraint';
import { ConstraintType } from 'models/ConstraintType';
import { AttributeType } from 'models/AttributeType';
import { Option, LabeledSelect } from 'components/Select';

type DoorsAttribute = Attribute & {
  validate: Validate;
  findWidthVariant: (doorSize: number) => number;
  calcDoorWidth: (width: number, doors: number) => number;
  widthMm: string;
  onBlur: () => void;
  inputs: { [key: string]: string };
  onSelectValue: (key: string, value: string) => void;
  autoAdjustValue: boolean;
  attributeType?: AttributeType;
};

export const Doors = connect<DoorsAttribute, Attribute, { inputs: { [key: string]: string } }>(
  state => ({
    inputs: getConfiguratorVariables(state.currentPage)
  }),
  dispatch => ({
    onBlur: () => {
      dispatch(onBlurField());
    },
    onSelectValue: (key: string, value: string) => {
      dispatch(onSelectValue(key, value));
    }
  }),
  ({ inputs, ...state }, dispatch, props) => ({
    ...state,
    ...dispatch,
    ...props,
    widthMm: inputs.widthMm || '0'
  })
)(function _Doors({
  findWidthVariant,
  validate,
  widthMm,
  calcDoorWidth,
  inputs,
  onSelectValue,
  autoAdjustValue,
  attributeType,
  ...props
}: DoorsAttribute) {
  const { onSelectChange } = useConfigurator();

  const selectDoors = (input: string, doorSize: number) => {
    onSelectChange(props.id, input);
    const variant = findWidthVariant(doorSize);
    variant && onSelectChange('widthMm', variant.toString(), { emit: false });
  };

  const setCorrectDoorWidth = (result: ValueConstraint) => {
    const totalWidth = parseFloat(widthMm);
    const _numberOfDoors = totalWidth / +result.value;
    const numberOfDoors =
      result.rule === ConstraintType.Max ? Math.ceil(_numberOfDoors) : Math.floor(_numberOfDoors);
    const doorSize = calcDoorWidth(totalWidth, numberOfDoors);

    const variant = findWidthVariant(doorSize);

    if (variant && autoAdjustValue) {
      onSelectChange(props.id, numberOfDoors.toString());
      onSelectChange('widthMm', variant.toString(), { emit: false });
    }
  };

  const setCorrectDoorAndWidth = (result: ValueConstraint, input: string) => {
    const doorWidth = calcDoorWidth(parseFloat(result.value.toString()), parseInt(input, 10));
    const variant = findWidthVariant(doorWidth);
    if (variant) {
      onSelectValue('widthMm', result.value.toString());
      onSelectChange('widthMm', variant.toString(), { emit: false });
      onSelectChange(props.id, input);
    }
  };

  const constraintAttributeMap: {
    [key: string]: (result: ValueConstraint, input: string) => void;
  } = {
    doors: result => {
      const numberOfDoors = +result.value;
      const totalWidth = parseFloat(widthMm);
      const doorSize = calcDoorWidth(totalWidth, numberOfDoors);
      const variant = findWidthVariant(doorSize);
      onSelectValue(result.attribute, result.value.toString());

      if (variant) {
        onSelectChange('widthMm', variant.toString(), { emit: false });
      }
    },
    widthMm: setCorrectDoorAndWidth,
    doorWidth: setCorrectDoorWidth
  };

  const onChange = (input: string) => {
    const doorSize = calcDoorWidth(parseFloat(widthMm), parseInt(input, 10));
    const validationArgs = [
      ['doors', input],
      ['widthMm', widthMm],
      ['doorWidth', doorSize.toString(), input]
    ];

    const result =
      Number.isInteger(doorSize) && Boolean(input)
        ? validationArgs.reduce(
            (result: IConstraint | undefined, [id, value, ...rest]) =>
              result !== undefined ? result : validate(id, value, ...rest),
            undefined
          )
        : undefined;

    if (!result) {
      selectDoors(input, doorSize);
    } else if (isValueConstraint(result)) {
      const fn = constraintAttributeMap[result.attribute];
      fn && fn(result, input);
    }
  };

  const onBlur = (input: string) => {
    onChange(input);
    setTimeout(props.onBlur, 0);
  };

  if (attributeType === AttributeType.Select) {
    return (
      <LabeledSelect
        label={props.name}
        isSelected={!!props.selected}
        onChange={onBlur}
        selectedValue={props.selected}
        labelFormat={`{0} ${props.unit || ''}`}
        scrollable
      >
        {props.values.map(value => (
          <Option key={value.value} {...value} />
        ))}
      </LabeledSelect>
    );
  }

  return <NumericInput {...props} onChange={onChange} onBlur={onBlur} />;
});
